import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowRight, Factory, Target, Recycle, FlaskConical, Droplets, Leaf, Shell } from 'lucide-react';

const services = [
  {
    icon: Factory,
    title: 'Commercial Biogas-CBG',
    description: 'Full-scale facilities for continuous production of Raw biogas and CBG for long-term revenue generation. RENVITEK delivers comprehensive, end-to-end engineering and consulting solutions for the Biogas and Compressed Biogas (CBG) industry.',
    link: '/services/biogas',
    features: [
      'Pre-feasibility studies and DPR preparation',
      'Waste analysis and substrate characterization',
      'BGP and BMP testing',
      'Process optimization and troubleshooting',
      'Comprehensive O&M support'
    ]
  },
  {
    icon: Target,
    title: 'RBHM™ Technology',
    description: 'RENVITEK\'s proprietary biodigester health intelligence and decision-support framework. RBHM ensures stable, efficient, and long-term operation of anaerobic digesters in Biogas and CBG plants.',
    link: '/services/rbhm',
    features: [
      'Biological health indicators monitoring',
      'Process performance analytics',
      'Early warning & predictive control',
      'Operational decision support',
      'Remote monitoring capabilities'
    ]
  },
  {
    icon: Recycle,
    title: 'RRR Prototype DIGESTER™',
    description: 'Resource · Recovery · Reuse Digester - RENVITEK\'s proprietary next-generation anaerobic digestion system for decentralized organic waste treatment with simultaneous recovery of biogas energy and nutrient-rich digestate.',
    link: '/services/RRRProto',
    features: [
      'High-efficiency anaerobic digestion',
      'Compatible with mixed feedstocks',
      'Compact, modular design',
      'Odourless, noiseless operation',
      'RBHM technology integration'
    ]
  },
  {
    icon: FlaskConical,
    title: 'Biogas Lab Services',
    description: 'Comprehensive analytical and bioprocess development services for the anaerobic digestion sector. Our advanced laboratories deliver precise, data-driven analysis of AD and RNG feedstocks.',
    link: '/services/lab',
    features: [
      '35+ analytical parameters',
      'BMP & BGP testing',
      'SMA testing',
      'Feedstock characterization',
      'Process optimization support'
    ]
  },
  
  {
    icon: Leaf,
    title: 'Composting Systems',
    description: 'Organic waste composting solutions including BIOGUEST-V, OWC, and home composting systems. Sustainable solutions for on-site waste management aligned with circular economy principles.',
    link: '/services/composting',
    features: [
      'BIOGUEST-V in-vessel composting',
      '24-Hour automatic waste converters',
      'Semi-automatic systems',
      'Home composting solutions',
      'NAAC compliance support'
    ]
  },
  {
    icon: Droplets,
    title: 'Water & Wastewater Solutions',
    description: 'End-to-end water and wastewater treatment solutions for residential, commercial, institutional, and industrial sectors. From raw water treatment to wastewater recycling and ZLD.',
    link: '/services/water',
    features: [
      'Sewage Treatment Plants (STP)',
      'Effluent Treatment Plants (ETP)',
      'Reverse Osmosis (RO) Plants',
      'Zero Liquid Discharge (ZLD)',
      'MEE and ATFD systems'
    ]
  },
  {
    icon: Shell,
    title: 'Biomass Briquettes & Pellets',
    description: 'Biomass briquettes and pellets are produced from renewable organic residues such as agricultural by-products, forestry waste, and wood-based biomass. These fuels offer high calorific value, consistent combustion characteristics, and reduced emissions, making them suitable for a wide range of thermal and energy applications.',
    link: '/services/biomass',
    features: [
      'Renewable and carbon-neutral energy source',
      'Uniform size and controlled combustion behavior',
      'Reduced ash and sulfur emissions compared to coal',
      'Lower operating costs and fuel price volatility',
      'Supports waste-to-energy and circular economy principles'
    ]
  }
];

export const ServicesPage = () => {
  return (
    <div className="min-h-screen pt-20" data-testid="services-page">
      {/* Hero Section */}
      <section className="bg-emerald-950 py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            className="text-center"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <span className="text-amber-400 uppercase tracking-[0.3em] text-sm font-semibold">
              What We Do
            </span>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mt-4 mb-6">
              Our Services
            </h1>
            <p className="text-white/70 text-lg max-w-3xl mx-auto">
              Comprehensive waste-to-energy and environmental solutions powered by scientific rigor, 
              technological innovation, and responsible engineering practices.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Services List */}
      <section className="py-12 md:py-10 bg-white dark:bg-emerald-950">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="space-y-16">
            {services.map((service, index) => (
              <motion.div
                key={service.title}
                className={`grid lg:grid-cols-2 gap-12 items-center ${
                  index % 2 === 1 ? 'lg:flex-row-reverse' : ''
                }`}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
              >
                <div className={index % 2 === 1 ? 'lg:order-2' : ''}>
                  <div className="flex items-center gap-4 mb-6">
                    <div className="w-14 h-14 bg-emerald-100 dark:bg-emerald-900 rounded-full flex items-center justify-center">
                      <service.icon className="w-7 h-7 text-emerald-600 dark:text-emerald-400" />
                    </div>
                    <h2 className="text-2xl md:text-3xl font-bold text-emerald-900 dark:text-white">
                      {service.title}
                    </h2>
                  </div>
                  <p className="text-slate-600 dark:text-white/70 leading-relaxed mb-6">
                    {service.description}
                  </p>
                  <ul className="space-y-3 mb-8">
                    {service.features.map((feature) => (
                      <li key={feature} className="flex items-start gap-3">
                        <div className="w-1.5 h-1.5 bg-amber-500 rounded-full mt-2" />
                        <span className="text-slate-600 dark:text-white/70">{feature}</span>
                      </li>
                    ))}
                  </ul>
                  <Link to={service.link}>
                    <motion.button
                      className="btn-primary flex items-center gap-2"
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      data-testid={`service-btn-${service.title.toLowerCase().replace(/\s+/g, '-')}`}
                    >
                      Learn More
                      <ArrowRight size={18} />
                    </motion.button>
                  </Link>
                </div>
                <div className={`bg-slate-100 dark:bg-emerald-900/50 p-8 ${index % 2 === 1 ? 'lg:order-1' : ''}`}>
                  <service.icon className="w-32 h-32 text-emerald-600/20 dark:text-emerald-400/20 mx-auto" />
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-12 md:py-10 bg-amber-500">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-emerald-950 mb-6">
            Ready to Get Started?
          </h2>
          <p className="text-emerald-900 text-lg mb-8">
            Contact us today to discuss your project requirements and how we can help.
          </p>
          <Link to="/contact">
            <motion.button
              className="bg-emerald-900 hover:bg-emerald-800 text-white px-10 py-4 uppercase tracking-wider font-semibold"
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              data-testid="services-cta-btn"
            >
              Contact Us
            </motion.button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default ServicesPage;
